import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { take } from 'rxjs/operators';
import { Logger, LoggingService } from 'ionic-logging-service';
import { ReturnUrlService } from '@app/services/return-url/return-url.service';
import { ShareExtensionService } from '@app/services/share-extension/share-extension.service';

/**
 * Owns where a user is sent once authentication has succeeded.
 *
 * Three pages complete a sign-in — AuthPage (web), LoginPage (native) and TenantSelectPage
 * (after choosing a tenant) — and each has to make the same decision in the same order:
 * a registration code from the URL wins, then a pending share intent, then the route the
 * auth guard interrupted, else the print-jobs list. The two that win outright both drop the
 * stash on their way past, or it would fire on a later, unrelated sign-in. Keeping that
 * order in one place is the point: it was previously spelled out three times, so any change
 * to it had to be made three times and would drift.
 *
 * What the pages still decide for themselves is which *destination* applies — whether the
 * user has tenant access, whether a tenant must be chosen — because those answers differ
 * per page. This service only executes the shared consequence.
 */
@Injectable({
  providedIn: 'root'
})
export class PostAuthNavigationService {

  private readonly DEFAULT_ROUTE = '/print-jobs';
  private readonly SHARE_INTENT_ROUTE = '/document-print';
  private readonly CARD_REGISTRATION_ROUTE = '/go';

  private logger: Logger;

  constructor(
    private loggingService: LoggingService,
    private navController: NavController,
    private returnUrlService: ReturnUrlService,
    private shareExtensionService: ShareExtensionService
  ) {
    this.logger = this.loggingService.getLogger('[PostAuthNavigationService]');
    const methodName = 'ctor';
    this.logger.entry(methodName);
  }

  /**
   * Sends the user to the card-registration page with the code the URL carried.
   *
   * A registration code is the current URL's explicit intent, so it wins over any stashed
   * return URL — but the stash is still dropped, or it would survive this sign-in and fire
   * on the next one, sending the user somewhere they never asked for.
   *
   * @param registrationCode - The code from the sign-in URL
   */
  public navigateToCardRegistration(registrationCode: string): void {
    const methodName = 'navigateToCardRegistration()';
    this.logger.info(methodName, 'Registration code takes precedence over any stashed route');
    this.discardReturnUrl();
    this.navController.navigateRoot([this.CARD_REGISTRATION_ROUTE], {
      queryParams: { registrationCode }
    });
  }

  /**
   * Returns the user to the route the auth guard interrupted, else the usual landing page.
   *
   * navigateRoot is given a string rather than an array, so the stashed query string
   * survives the navigation.
   *
   * A pending share intent outranks the stash and drops it, as a registration code does. Not
   * because it is newer, but because the costs are asymmetric: a consent link that loses is
   * still in the user's inbox, a shared file that loses is gone. ShareExtensionService cannot
   * navigate while signed out, so this is the only thing standing between a shared document
   * and being dropped.
   */
  public navigateIntoApp(): void {
    const methodName = 'navigateIntoApp()';

    if (this.shareExtensionService.pendingShareIntent()) {
      this.logger.info(methodName, 'Pending share intent takes precedence over any stashed route');
      this.discardReturnUrl();
      this.navigateToDefaultLanding();
      return;
    }

    this.returnUrlService.consume().pipe(take(1)).subscribe({
      next: (returnUrl: string | null) => {
        if (returnUrl) {
          this.logger.info(methodName + ' returning to interrupted route');
          this.navController.navigateRoot(returnUrl);
          return;
        }
        this.navigateToDefaultLanding();
      },
      // A read failure must not strand the user on the page they signed in from.
      error: () => this.navigateToDefaultLanding()
    });
  }

  /**
   * Drops any stashed return URL without navigating anywhere.
   *
   * For terminal outcomes such as "no tenant access": the session ends here, so a surviving
   * stash would fire on a later, unrelated sign-in.
   */
  public discardReturnUrl(): void {
    this.returnUrlService.clear().pipe(take(1)).subscribe();
  }

  // ############### INTERNAL ###############

  private navigateToDefaultLanding(): void {
    if (this.shareExtensionService.pendingShareIntent()) {
      this.navController.navigateRoot([this.SHARE_INTENT_ROUTE]);
      return;
    }
    this.navController.navigateRoot([this.DEFAULT_ROUTE]);
  }
}
